import { useCallback, useEffect, useRef, useState } from 'react';

export const DEFAULT_TRANSIENT_STATUS_MS = 3500;

const EMPTY_STATUS = Object.freeze({ type: '', message: '' });

export function useTransientStatus({ successDurationMs = DEFAULT_TRANSIENT_STATUS_MS } = {}) {
  const [status, setStatus] = useState(EMPTY_STATUS);
  const timerRef = useRef(null);
  const sequenceRef = useRef(0);

  const cancelTimer = useCallback(() => {
    if (timerRef.current !== null) {
      window.clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const clear = useCallback(() => {
    sequenceRef.current += 1;
    cancelTimer();
    setStatus(EMPTY_STATUS);
  }, [cancelTimer]);

  const show = useCallback(({ type = '', message = '', transient = type === 'success' } = {}) => {
    sequenceRef.current += 1;
    const sequence = sequenceRef.current;
    cancelTimer();

    const normalizedMessage = String(message || '').trim();
    if (!normalizedMessage) {
      setStatus(EMPTY_STATUS);
      return;
    }

    setStatus({ type: String(type || ''), message: normalizedMessage });
    if (!transient || successDurationMs <= 0) return;

    timerRef.current = window.setTimeout(() => {
      timerRef.current = null;
      if (sequenceRef.current !== sequence) return;
      setStatus(EMPTY_STATUS);
    }, successDurationMs);
  }, [cancelTimer, successDurationMs]);

  useEffect(() => () => {
    sequenceRef.current += 1;
    cancelTimer();
  }, [cancelTimer]);

  return { ...status, show, clear };
}
